import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { TransactionsService } from '../../../shared/services/transactions.service';
import {
  METADATA_TYPE_LABEL,
  SOURCE_SERVICE_LABEL,
  TRANSACTION_STATUS_META,
  Transaction,
  TransactionMetadata,
  parseTransactionMetadata,
} from '../../../shared/types/transaction.types';

@Component({
  selector: 'app-transaction-detail',
  imports: [
    CommonModule,
    RouterModule,
    LocalizedDatePipe,
    DecimalPipe,
    PageHeaderComponent,
    DataStateComponent,
    UserCellComponent,
  ],
  templateUrl: './transaction-detail.component.html',
})
export class TransactionDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly txService = inject(TransactionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly tx = signal<Transaction | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  readonly statusMeta = TRANSACTION_STATUS_META;
  readonly sourceLabel = SOURCE_SERVICE_LABEL;
  readonly metaLabel = METADATA_TYPE_LABEL;

  /** Parsed metadata — refreshed when tx() changes. */
  readonly metadata = computed<TransactionMetadata | null>(() =>
    parseTransactionMetadata(this.tx()?.metadata),
  );

  /** Pretty type label from metadata, fallback empty. */
  readonly metadataTypeLabel = computed<string>(() => {
    const m = this.metadata();
    if (!m?.type) return '';
    return METADATA_TYPE_LABEL[m.type] ?? m.type;
  });

  /**
   * Build a router link to the source entity, or `null` if not navigable.
   * Convention: `sourceService=rental` + entityType `INVOICE`/`RENTAL` → `/invoices/:id` / `/rentals/:id`.
   */
  readonly sourceLink = computed<{ link: unknown[]; label: string } | null>(() => {
    const t = this.tx();
    if (!t) return null;
    const id = t.sourceEntityId;
    const type = (t.sourceEntityType ?? '').toUpperCase();
    if (!id) return null;
    if (t.sourceService === 'rental') {
      if (type === 'INVOICE' || type === 'INVOICES') return { link: ['/invoices', id], label: `Facture #${id}` };
      if (type === 'RENTAL' || type === 'RENTALS') return { link: ['/rentals', id], label: `Location #${id}` };
    }
    return null;
  });

  /** Best-effort: extract `invoiceNumber` from metadata for the RENTAL type. */
  readonly metadataInvoiceNumber = computed<string | null>(() => {
    const m = this.metadata();
    if (m && m.type === 'RENTAL' && typeof m['invoiceNumber'] === 'string') {
      return m['invoiceNumber'];
    }
    return null;
  });

  /** Recipient display fallback chain. */
  readonly recipientLabel = computed<string>(() => {
    const t = this.tx();
    if (!t) return '—';
    const name = [t.recipientFirstName, t.recipientLastName].filter(Boolean).join(' ').trim();
    return name || t.recipientNumber || t.recipientEmail || '—';
  });

  /** Pretty JSON for the metadata "raw" view, falls back to the original string. */
  readonly metadataPretty = computed<string | null>(() => {
    const raw = this.tx()?.metadata;
    if (!raw) return null;
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  });

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.txService
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (t) => {
          this.tx.set(t);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cette transaction.'),
          );
        },
      });
  }

  // ── Type-safe metadata field accessors for the template ───────────────
  // (used only when this.metadata() has a known shape — keeps the template free of casts)

  metaField(key: string): string | number | undefined {
    const m = this.metadata();
    if (!m) return undefined;
    const v = m[key];
    if (typeof v === 'string' || typeof v === 'number') return v;
    return undefined;
  }
}
